import React from 'react';
import { useServices } from '../hooks/useContentful';

const ServicesSection = () => {
  const { services, loading, error } = useServices();

  if (loading) {
    return (
      <section id="servicios" className="py-12 md:py-20 bg-gray-50 relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-300 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="text-center mb-10 md:mb-16">
            <div className="h-8 md:h-10 bg-gradient-to-r from-purple-200 via-purple-300 to-purple-200 rounded-lg w-64 mx-auto mb-4 animate-shimmer-purple"></div>
            <div className="h-6 bg-gradient-to-r from-purple-100 via-purple-200 to-purple-100 rounded-lg w-96 mx-auto animate-shimmer-purple" style={{ animationDelay: '0.3s' }}></div>
          </div>
          {/* Cards Grid Skeleton */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-purple-50 rounded-2xl p-6 md:p-8 shadow-md border border-purple-200 animate-pulse" style={{ animationDelay: `${index * 0.05}s` }}>
                {/* Icon Skeleton */}
                <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-purple-300 to-purple-400 rounded-full mb-6 animate-glow"></div>
                {/* Title Skeleton */}
                <div className="h-6 bg-gradient-to-r from-purple-200 to-purple-300 rounded w-3/4 mb-3 animate-shimmer-purple"></div>
                {/* Text Skeleton */}
                <div className="space-y-2 mb-4">
                  <div className="h-4 bg-gradient-to-r from-purple-100 to-purple-200 rounded w-full animate-shimmer-purple"></div>
                  <div className="h-4 bg-gradient-to-r from-purple-100 to-purple-200 rounded w-5/6 animate-shimmer-purple"></div>
                </div>
                {/* Decorative line skeleton */}
                <div className="mt-4 h-1 w-0 bg-gradient-to-r from-purple-600 to-purple-500"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error || !services.length) {
    return (
      <section id="servicios" className="py-20 bg-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-red-600">Error al cargar los servicios</h2>
          <p className="text-gray-600 mt-2">
            Intenta recargar la página o contacta al administrador.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="servicios" className="py-12 md:py-20 bg-gradient-to-b from-purple-50 via-white to-pink-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-0 w-80 h-80 bg-purple-200/20 rounded-full -mr-40 blur-3xl animate-float"></div>
      <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-200/15 rounded-full -ml-48 blur-3xl animate-float" style={{ animationDelay: '1s' }}></div>

      {/* Section Separator */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-400 to-pink-400 to-transparent"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-20 animate-fadeInUp">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="fas fa-paw"></i>
            Nuestros Servicios
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-800 mb-4 md:mb-6 leading-tight">
            Servicios de <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">excelencia</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto px-4 leading-relaxed">
            Ofrecemos atención veterinaria especializada para emergencias y cuidados preventivos, con un enfoque en la calidad y la compasión.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
          {services.map((service, index) => (
            <div
              key={service.id || index}
              className="group bg-white rounded-3xl p-8 md:p-10 service-card animate-fadeInUp cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full relative overflow-hidden border border-purple-200/50 hover:border-purple-300"
              style={{ animationDelay: `${0.6 + index * 0.1}s` }}
            >
              {/* Decorative icon */}
              <div className="absolute top-4 right-4 text-purple-200 text-4xl opacity-30 group-hover:opacity-50 transition-opacity">
                <i className="fas fa-paw"></i>
              </div>
              
              <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mb-6 shadow-md group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-pink-600 transition-all duration-500 group-hover:scale-110 relative z-10">
                <i className={`${service.icon} text-purple-600 text-3xl group-hover:text-white transition-colors duration-500`}></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-3 group-hover:text-purple-700 transition-colors duration-300 relative z-10">{service.titleIcon}</h3>
              <p className="text-sm md:text-base text-gray-700 group-hover:text-gray-800 transition-colors duration-300 flex-grow relative z-10">{service.description}</p>

              {/* Decorative element */}
              <div className="mt-6 w-0 h-1 bg-gradient-to-r from-purple-600 to-pink-600 group-hover:w-full transition-all duration-500"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
